/**
 * FrameCropPreview — renders a thumbnail of the exact pixel region the
 * backend's `_crop_frame_from_photo` will cut from the layout's
 * reference photo for a single frame.
 *
 * The maths here MUST stay in sync with `certificates.py:_crop_frame_from_photo`.
 * Both treat frame x/y/w/h as percentages of the frontend's fixed 4:3
 * `object-contain` container, then translate them onto the photo's
 * natural pixels (accounting for letterbox bars on non-4:3 photos).
 *
 * Used by `BoardLayoutEditor.jsx`:
 *  - inline "what will the AI see for this frame" preview
 *  - "Verify alignment" grid of all frames at once
 */
import React, { useEffect, useRef, useState } from 'react';

const CONTAINER_AR = 4 / 3;

// Single shared off-screen <img> per-photoUrl so a 50-frame grid
// doesn't kick off 50 simultaneous decode passes.
const imageCache = new Map();
function loadImage(src) {
  if (!src) return Promise.resolve(null);
  if (imageCache.has(src)) return imageCache.get(src);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
  imageCache.set(src, p);
  return p;
}

/**
 * Project (frame %, photo natural size) -> source pixel rect using the
 * same `object-contain` layout the editor canvas applies.
 * Returns {sx, sy, sw, sh} or null if degenerate.
 */
function projectFrameToPixels(frame, iw, ih) {
  if (!iw || !ih) return null;
  const cw = 1.0;
  const ch = 1.0 / CONTAINER_AR;
  const fx = (frame.x ?? 0) / 100 * cw;
  const fy = (frame.y ?? 0) / 100 * ch;
  const fw = Math.max(0.001, (frame.w ?? 1) / 100) * cw;
  const fh = Math.max(0.001, (frame.h ?? 1) / 100) * ch;
  const photoAR = iw / ih;
  let dispW, dispH, offX, offY;
  if (photoAR >= CONTAINER_AR) {
    dispW = cw;
    dispH = cw / photoAR;
    offX = 0;
    offY = (ch - dispH) / 2;
  } else {
    dispH = ch;
    dispW = ch * photoAR;
    offX = (cw - dispW) / 2;
    offY = 0;
  }
  const relX = fx - offX;
  const relY = fy - offY;
  let px0 = relX / dispW * iw;
  let py0 = relY / dispH * ih;
  let px1 = (relX + fw) / dispW * iw;
  let py1 = (relY + fh) / dispH * ih;
  px0 = Math.max(0, Math.min(iw, px0));
  py0 = Math.max(0, Math.min(ih, py0));
  px1 = Math.max(0, Math.min(iw, px1));
  py1 = Math.max(0, Math.min(ih, py1));
  const sw = Math.max(1, Math.round(px1 - px0));
  const sh = Math.max(1, Math.round(py1 - py0));
  if (sw < 2 || sh < 2) return null;
  return { sx: Math.round(px0), sy: Math.round(py0), sw, sh };
}

export const FrameCropPreview = ({
  photoUrl,
  frame,
  maxWidth = 220,
  maxHeight = 140,
  className = '',
  emptyLabel = 'No photo',
  ...rest
}) => {
  const canvasRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [outOfBounds, setOutOfBounds] = useState(false);

  useEffect(() => {
    let alive = true;
    setImgLoaded(false);
    setOutOfBounds(false);
    if (!photoUrl || !frame || !canvasRef.current) return undefined;
    loadImage(photoUrl).then((img) => {
      if (!alive || !img || !canvasRef.current) return;
      const rect = projectFrameToPixels(frame, img.naturalWidth, img.naturalHeight);
      if (!rect) {
        setOutOfBounds(true);
        return;
      }
      // Scale the canvas to fit within maxWidth/maxHeight while keeping
      // the crop's true aspect ratio.
      const scale = Math.min(maxWidth / rect.sw, maxHeight / rect.sh, 1);
      const dw = Math.max(2, Math.round(rect.sw * scale));
      const dh = Math.max(2, Math.round(rect.sh * scale));
      const canvas = canvasRef.current;
      canvas.width = dw;
      canvas.height = dh;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, dw, dh);
      ctx.drawImage(img, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, dw, dh);
      setImgLoaded(true);
    });
    return () => { alive = false; };
  }, [photoUrl, frame?.x, frame?.y, frame?.w, frame?.h, maxWidth, maxHeight]);

  if (!photoUrl) {
    return (
      <div
        className={`flex items-center justify-center text-[10px] text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded ${className}`}
        style={{ minHeight: 48 }}
        {...rest}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div
      className={`relative bg-slate-900 rounded overflow-hidden ${className}`}
      style={{ minHeight: 32 }}
      {...rest}
    >
      <canvas ref={canvasRef} className="block max-w-full" />
      {!imgLoaded && !outOfBounds && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400">
          Loading…
        </div>
      )}
      {outOfBounds && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] text-amber-300 bg-slate-900/80 px-1 text-center">
          Frame outside photo
        </div>
      )}
    </div>
  );
};

// Re-export the maths so tests / other components can reuse it.
export { projectFrameToPixels };

export default FrameCropPreview;
